import { el } from "./dom.js";
import { MAG, magGlass, magButton } from "./magazine/kit.js";
import { magField } from "./magazine/detail.js";
import { updateMediaItem } from "../db/media.js";
import { rerenderRoute } from "../main.js";
import { showToast } from "./toast.js";
import { progressUnits, normalizeProgress, progressPct, progressLabel, normalizeLoan, loanDays } from "../utils/tracking.js";
import { todayDate } from "../utils/dates.js";

// Tracking depth on a detail page (roadmap Batch 7): progress (#28), Start
// again (#29), a price note on wishlist items (#30) and loans (#32). Each saves
// through the item's own update, so History and sync see it.

const inputStyle = {
	width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,.06)", border: `1px solid ${MAG.line}`, borderRadius: "8px",
	padding: "8px 10px", color: MAG.ink, fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "13px", outline: "none",
};
const small = (t) => el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px", letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim, marginBottom: "4px" } }, t);

async function save(mediaType, id, patch, what) {
	try {
		await updateMediaItem(mediaType, id, patch);
	} catch (err) {
		showToast(`Couldn't save ${what}: ${err?.message ?? err}`);
	}
}

// A thin progress bar (also used on Home).
export function progressBar(p, { color = MAG.lime, height = "5px" } = {}) {
	const pct = progressPct(p);
	if (pct == null) return "";
	return el("div", { style: { height, borderRadius: height, background: "rgba(255,255,255,.14)", overflow: "hidden" } },
		el("div", { style: { width: `${pct}%`, height: "100%", background: color } }));
}

export function progressField(mediaType, item, { index = 2 } = {}) {
	const units = progressUnits(mediaType);
	if (!units) return "";
	let p = item.progress ?? null;
	const unitSel = el("select", { style: { ...inputStyle, width: "auto" } }, ...units.map((u) => el("option", { value: u, selected: (p?.unit ?? units[0]) === u }, u)));
	const cur = el("input", { type: "number", min: "0", step: "any", inputmode: "decimal", value: p?.current ?? "", placeholder: "0", style: inputStyle });
	const tot = el("input", { type: "number", min: "0", step: "any", inputmode: "decimal", value: p?.unit === "%" ? "" : (p?.total ?? ""), placeholder: "of…", style: inputStyle });
	const barSlot = el("div", { style: { marginTop: "10px" } });
	const label = el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "12px", color: MAG.ink, marginTop: "6px" } });
	const paint = () => {
		tot.disabled = unitSel.value === "%";
		tot.style.opacity = tot.disabled ? ".4" : "1";
		barSlot.replaceChildren(progressBar(p) || "");
		label.textContent = p ? progressLabel(p) : "";
	};
	const commit = async () => {
		const next = normalizeProgress({ current: cur.value, total: tot.value, unit: unitSel.value }, mediaType);
		if (JSON.stringify(next) === JSON.stringify(p)) return paint();
		p = next;
		paint();
		await save(mediaType, item.id, { progress: next }, "progress");
	};
	for (const x of [cur, tot]) x.addEventListener("change", commit);
	unitSel.addEventListener("change", commit);
	paint();
	return magField("Progress", magGlass([
		el("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "8px", alignItems: "end" } },
			el("div", {}, small("At"), cur),
			el("div", {}, small("Of"), tot),
			el("div", {}, small("Unit"), unitSel)),
		barSlot,
		label,
	], { style: { padding: "12px 14px" } }), { index });
}

// "Start again" on something finished: a new pass (Rewatching / Rereading /
// Replaying). The finish is kept in History; finishing again adds another.
export function startAgainButton(mediaType, item) {
	if (item.engagement_status !== "done") return "";
	const verb = mediaType === "book" || mediaType === "comic" ? "reading" : mediaType === "videogame" ? "playing" : "watching";
	return el("div", { style: { padding: "8px 16px 0" } },
		magButton(`↻ Start again (${verb} it another time)`, {
			variant: "ghost",
			block: true,
			onClick: async () => {
				const patch = { engagement_status: "replaying" };
				if (item.progress) patch.progress = { ...item.progress, current: 0 };
				await save(mediaType, item.id, patch, "the new pass");
				showToast("New pass started — it's in History.");
				rerenderRoute();
			},
		}));
}

export function priceNoteField(mediaType, item, { index = 4 } = {}) {
	if (item.ownership_status !== "want_it") return "";
	const input = el("input", {
		type: "text",
		maxlength: "120",
		value: item.price_note ?? "",
		placeholder: "e.g. “wait for €20”, “€45 at the shop on Main St”",
		style: inputStyle,
	});
	input.addEventListener("change", () => save(mediaType, item.id, { price_note: input.value.trim() || null }, "the price note"));
	return magField("Price note", magGlass([input], { style: { padding: "10px 12px" } }), { index });
}

export function loanField(mediaType, item, { index = 3 } = {}) {
	if (item.ownership_status !== "have_it" && !item.loan) return "";
	if (!item.loan) {
		// Nothing saved until a name is typed.
		const wrap = el("div", { style: { padding: "8px 16px 0" } });
		wrap.append(magButton("Lent it to someone?", {
			variant: "ghost",
			block: true,
			onClick: () => {
				const form = loanForm(mediaType, item, { to: "", since: todayDate(), remind_on: null }, index);
				wrap.replaceWith(form);
				form.querySelector("input")?.focus();
			},
		}));
		return wrap;
	}
	return loanForm(mediaType, item, item.loan, index);
}

function loanForm(mediaType, item, loan, index) {
	const to = el("input", { type: "text", maxlength: "60", value: loan.to ?? "", placeholder: "Who has it?", style: inputStyle });
	const since = el("input", { type: "date", value: loan.since ?? "", style: inputStyle });
	const remind = el("input", { type: "date", value: loan.remind_on ?? "", style: inputStyle });
	const days = loanDays(loan, todayDate());
	const commit = async () => {
		const next = normalizeLoan({ to: to.value, since: since.value, remind_on: remind.value });
		if (next) await save(mediaType, item.id, { loan: next }, "the loan");
	};
	for (const x of [to, since, remind]) x.addEventListener("change", commit);
	return magField("Lent out", magGlass([
		small("To"), to,
		el("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px" } },
			el("div", {}, small("Since"), since),
			el("div", {}, small("Remind me on"), remind)),
		el("div", { style: { display: "flex", alignItems: "center", gap: "8px", marginTop: "10px" } },
			el("div", { style: { flex: "1", fontFamily: "'Archivo', sans-serif", fontSize: "11.5px", color: MAG.dim } },
				loan.to && days != null ? `${loan.to} has had it ${days === 0 ? "since today" : `${days} day${days === 1 ? "" : "s"}`}.` : "The bell reminds you on the day you pick."),
			magButton("Got it back", {
				variant: "ghost",
				onClick: async () => {
					await save(mediaType, item.id, { loan: null }, "the loan");
					rerenderRoute();
				},
			})),
	], { style: { padding: "12px 14px" } }), { index, color: "#ffe600" });
}
